<template>
  <v-card class="kiosk-main-wrapper fill-height d-flex flex-column" flat tile>
    <!-- 1. MODAL DE SELECCIÓN DE PANTALLA / TERMINAL CON DETECCIÓN EN VIVO -->
    <v-dialog v-model="showScreenConfigModal" persistent max-width="540">
      <v-card class="pa-5 rounded-2xl elevation-10">
        <v-card-title class="headline font-weight-bold primary--text pb-2">
          <v-icon left color="primary" size="32">mdi-tablet-dashboard</v-icon>
          Estación de Terminal de Firma
        </v-card-title>
        <v-card-text class="pt-2">
          <p class="body-2 grey--text text--darken-2 mb-3">
            Seleccione el número de Terminal asignado a este equipo. Los
            terminales en uso por otros navegadores están bloqueados:
          </p>

          <v-alert
            v-if="kioskInUseError"
            type="error"
            dense
            outlined
            class="mb-3 font-weight-medium"
          >
            <v-icon left small>mdi-alert-circle</v-icon>
            {{ kioskInUseError }}
          </v-alert>

          <!-- Lista de Terminales con indicador de estado -->
          <v-list dense class="py-0 mb-3 border rounded-xl overflow-hidden">
            <v-list-item
              v-for="scr in predefinedScreens"
              :key="scr"
              :class="{
                'primary lighten-5 font-weight-bold':
                  screenId === scr && !isScreenOnlineInOtherMachine(scr),
                'orange lighten-5 font-weight-bold':
                  screenId === scr && isScreenOnlineInOtherMachine(scr),
                'grey lighten-4':
                  isScreenOnlineInOtherMachine(scr) && screenId !== scr,
              }"
              @click="
                if (screenId !== scr) {
                  kioskInUseError = '';
                }
                screenId = scr;
              "
              class="cursor-pointer py-2 border-bottom-subtle"
            >
              <v-list-item-avatar size="36">
                <v-icon
                  :color="
                    screenId === scr
                      ? isScreenOnlineInOtherMachine(scr)
                        ? 'orange darken-3'
                        : 'primary'
                      : isScreenOnlineInOtherMachine(scr)
                      ? 'grey'
                      : 'grey darken-2'
                  "
                >
                  {{
                    isScreenOnlineInOtherMachine(scr)
                      ? "mdi-monitor-lock"
                      : "mdi-monitor-cellphone"
                  }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="subtitle-2">
                  {{ scr }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  {{
                    isScreenOnlineInOtherMachine(scr)
                      ? "En uso en otro equipo / sesión previa"
                      : "Disponible para esta estación"
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip
                  x-small
                  :color="
                    isScreenOnlineInOtherMachine(scr)
                      ? 'orange darken-3'
                      : 'success'
                  "
                  outlined
                  class="font-weight-bold"
                >
                  <v-icon x-small left>{{
                    isScreenOnlineInOtherMachine(scr)
                      ? "mdi-lock-clock"
                      : "mdi-check-circle"
                  }}</v-icon>
                  {{ isScreenOnlineInOtherMachine(scr) ? "En uso" : "Libre" }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="px-5 pb-3">
          <v-btn text color="grey darken-1" small @click="fetchQueueState">
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
          <v-spacer />
          <!-- Botón cuando el terminal está ocupado/zombie -->
          <v-btn
            v-if="
              screenId &&
              (isScreenOnlineInOtherMachine(screenId) || kioskInUseError)
            "
            color="orange darken-3"
            dark
            large
            rounded
            class="px-6 font-weight-bold text-none elevation-2"
            @click="saveAndConnectScreen(true)"
          >
            <v-icon left>mdi-account-switch</v-icon>
            Liberar y Reclamar {{ screenId }}
          </v-btn>
          <!-- Botón normal cuando está libre -->
          <v-btn
            v-else
            color="primary"
            large
            rounded
            class="px-6 font-weight-bold text-none elevation-2"
            :disabled="!screenId"
            @click="saveAndConnectScreen(false)"
          >
            <v-icon left>mdi-lan-connect</v-icon>
            Iniciar Estación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 2. HEADER KIOSCO -->
    <v-toolbar dense color="primary" dark class="elevation-2 flex-grow-0">
      <v-icon left>mdi-file-document-edit</v-icon>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold">
        SunExpert — Terminal Digital de Contratos
      </v-toolbar-title>

      <v-chip
        small
        color="white"
        text-color="primary"
        class="ml-3 font-weight-bold"
      >
        <v-icon x-small left color="primary">mdi-monitor</v-icon>
        {{ screenId || "SIN ASIGNAR" }}
      </v-chip>

      <v-spacer />

      <!-- Temporizador de lectura si está activo -->
      <v-chip
        v-if="kioskState === 'READING'"
        color="amber accent-3"
        text-color="black"
        class="font-weight-bold font-mono mr-3"
        small
      >
        <v-icon x-small left color="black">mdi-timer-outline</v-icon>
        Tiempo Restante: {{ formatTime(readingTimeRemaining) }}
      </v-chip>

      <div class="d-flex align-center gap-2">
        <v-btn icon small @click="toggleFullscreen" title="Pantalla Completa">
          <v-icon small>{{
            isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
          }}</v-icon>
        </v-btn>

        <v-badge
          dot
          :color="isSignalRConnected ? 'green accent-3' : 'red accent-3'"
          class="mr-2"
        >
          <v-icon small>{{
            isSignalRConnected ? "mdi-wifi" : "mdi-wifi-off"
          }}</v-icon>
        </v-badge>
        <span class="caption font-weight-medium">
          {{ isSignalRConnected ? "En línea" : "Desconectado" }}
        </span>
      </div>
    </v-toolbar>

    <!-- 3. ESTADO 1: EN ESPERA DE TURNO (IDLE) -->
    <div
      v-if="kioskState === 'IDLE'"
      class="flex-grow-1 d-flex align-center justify-center bg-kiosk-idle"
    >
      <div class="text-center pa-8 kiosk-idle-box rounded-2xl elevation-4">
        <v-avatar size="100" color="primary lighten-5" class="mb-4">
          <v-icon size="56" color="primary">mdi-account-clock-outline</v-icon>
        </v-avatar>
        <h2 class="text-h4 font-weight-black primary--text mb-2">
          {{ screenId }} Disponible
        </h2>
        <p class="text-subtitle-1 grey--text text--darken-2 mb-4">
          Esperando que se asigne el próximo colaborador en turno...
        </p>
        <v-chip
          color="green lighten-5"
          text-color="green darken-3"
          class="font-weight-bold"
        >
          <v-icon x-small left color="green darken-3">mdi-check-circle</v-icon>
          Estación Libre y Lista
        </v-chip>
      </div>
    </div>

    <!-- 4. ESTADO 2: LLAMADO CON TOLERANCIA DE 1 MINUTO -->
    <div
      v-else-if="kioskState === 'CALLED'"
      class="flex-grow-1 d-flex align-center justify-center bg-kiosk-called pa-6"
    >
      <v-card
        class="called-turn-box pa-8 rounded-2xl elevation-12 text-center max-w-700"
      >
        <v-chip
          color="amber darken-3"
          text-color="white"
          large
          class="font-weight-black mb-4"
        >
          <v-icon left>mdi-bullhorn</v-icon>
          ¡SU TURNO HA SIDO LLAMADO!
        </v-chip>

        <div
          class="turn-call-code text-h3 font-weight-black amber--text text--darken-3 mb-2 font-mono"
        >
          {{ currentTurn.TurnCode }}
        </div>

        <h2 class="text-h4 font-weight-bold text--primary mb-1">
          {{ currentTurn.FullName }}
        </h2>
        <div class="subtitle-1 grey--text text--darken-2 mb-6">
          DNI: <strong>{{ currentTurn.Dni }}</strong> | Puesto:
          <strong>{{ currentTurn.PositionName || "Operario" }}</strong>
        </div>

        <v-divider class="mb-6" />

        <!-- Contador de Tolerancia (1 Minuto) -->
        <div class="tolerance-box pa-4 rounded-xl mb-6">
          <div class="caption grey--text text--darken-2 font-weight-bold mb-1">
            TIEMPO PARA CONFIRMAR ASISTENCIA
          </div>
          <div
            class="text-h3 font-weight-black red--text text--darken-1 font-mono"
          >
            00:{{ toleranceTimeRemaining.toString().padStart(2, "0") }}
          </div>
          <div class="caption red--text text--darken-2 mt-1">
            Si no confirma en este tiempo, su turno volverá a la cola.
          </div>
        </div>

        <v-btn
          color="success darken-1"
          x-large
          rounded
          elevation="6"
          class="font-weight-black px-8 py-6 text-h6 animate-pulse"
          @click="startReadingSession"
        >
          <v-icon left large>mdi-file-eye-outline</v-icon>
          INICIAR LECTURA DE DOCUMENTOS
        </v-btn>
      </v-card>
    </div>

    <!-- 5. ESTADO 3: LECTURA Y REVISIÓN DE DOCUMENTOS PASO A PASO -->
    <div
      v-else-if="kioskState === 'READING'"
      class="kiosk-reading-wrapper flex-grow-1 d-flex flex-column overflow-hidden position-relative"
    >
      <!-- Barra superior del colaborador -->
      <div
        class="bg-worker-header px-4 py-2 border-bottom d-flex align-center justify-space-between flex-wrap gap-2 flex-grow-0"
      >
        <div class="d-flex align-center flex-wrap gap-3">
          <div>
            <span class="caption grey--text text--darken-1">Colaborador:</span>
            <strong class="ml-1 text-subtitle-2 primary--text">{{
              currentTurn.FullName
            }}</strong>
          </div>
          <v-divider vertical class="mx-1 my-1" />
          <div>
            <span class="caption grey--text text--darken-1">DNI:</span>
            <strong class="ml-1 font-mono">{{ currentTurn.Dni }}</strong>
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-chip
            small
            color="amber lighten-4"
            text-color="amber darken-4"
            class="font-weight-bold"
          >
            <v-icon x-small left color="amber darken-4">mdi-marker</v-icon>
            Datos clave resaltados en amarillo
          </v-chip>
          <v-chip small color="primary" outlined class="font-weight-bold">
            Paso {{ activeDocIndex + 1 }} de {{ documentsList.length }}:
            {{ completedDocsCount }} Aceptado(s)
          </v-chip>
        </div>
      </div>

      <!-- Barra de Pasos Progresivos (Stepper Restrictivo) -->
      <div
        class="kiosk-stepper-bar px-4 py-2 bg-white border-bottom d-flex align-center gap-2 overflow-x-auto flex-grow-0"
      >
        <div
          v-for="(doc, idx) in documentsList"
          :key="idx"
          class="kiosk-step-pill d-flex align-center px-3 py-1 rounded-pill cursor-pointer transition-all"
          :class="{
            'step-active': activeDocIndex === idx,
            'step-completed': isDocAccepted(doc.DocumentId),
            'step-locked': !isStepAccessible(idx),
          }"
          @click="goToStep(idx)"
        >
          <v-icon x-small left :color="getStepIconColor(idx, doc)">
            {{ getStepIcon(idx, doc) }}
          </v-icon>
          <span class="caption font-weight-bold">
            {{ idx + 1 }}. {{ doc.Title }}
          </span>
        </div>
      </div>

      <!-- Contenedor con Scroll para el Documento -->
      <div
        class="flex-grow-1 overflow-y-auto pa-4 bg-doc-canvas doc-scroll-container position-relative"
        ref="docScrollContainer"
        :key="'doc-scroll-step-' + activeDocIndex"
        @scroll="handleDocScroll"
      >
        <div ref="docTopAnchor" style="height: 1px; width: 100%"></div>
        <v-container
          class="bg-white rounded-lg elevation-1 pa-6 doc-content-wrapper"
          :class="{
            'doc-content-wide':
              currentDoc &&
              (currentDoc.ComponentName === 'FormatDigitalConsentAri' ||
                currentDoc.ComponentName ===
                  'FormatDigitalConsentAriDeliverable'),
          }"
        >
          <component
            v-if="
              currentDoc &&
              currentDoc.DocumentType === 'CONTRATO_TRABAJO' &&
              fullContractData
            "
            :is="currentDoc.ComponentName"
            :data="fullContractData"
            :prevent-individual-download="true"
            :show-salary="true"
            :showSalary="true"
          />
          <component
            v-else-if="currentDoc && currentDoc.ComponentName"
            :is="currentDoc.ComponentName"
            :key="currentDoc.ComponentName + '-' + activeDocIndex"
            :worker-data="fullContractData || currentTurn"
            :contract-data="fullContractData || currentTurn"
            :date-value="contractDate"
            :prevent-individual-download="true"
          />
          <div v-else class="text-center py-10">
            <v-icon size="48" color="primary" class="mb-3"
              >mdi-file-document-check-outline</v-icon
            >
            <h3 class="text-h6 font-weight-bold primary--text mb-2">
              {{ currentDoc && currentDoc.Title }}
            </h3>
            <p class="body-2 grey--text">
              Por favor revise detalladamente los términos y condiciones de este
              documento antes de marcar la casilla de aceptación.
            </p>
          </div>
        </v-container>
      </div>

      <!-- Barra Inferior Fija de Aceptación y Navegación Paso a Paso -->
      <div
        class="kiosk-action-footer px-6 py-3 border-top bg-white d-flex align-center justify-space-between flex-wrap gap-3 flex-grow-0"
      >
        <!-- Lado Izquierdo: Botón / Checkbox de Aceptación -->
        <div class="d-flex align-center">
          <v-card
            flat
            outlined
            class="px-4 py-2 rounded-lg cursor-pointer transition-all border-accept"
            :class="
              currentDocAccepted
                ? 'green lighten-5 border-success'
                : 'grey lighten-4'
            "
            @click="toggleAcceptanceCard"
          >
            <div class="d-flex align-center">
              <v-checkbox
                v-model="currentDocAccepted"
                color="success"
                hide-details
                class="ma-0 pa-0 mr-2"
                @change="onToggleCurrentDocAccepted"
              />
              <div>
                <div
                  class="body-2 font-weight-bold"
                  :class="
                    currentDocAccepted
                      ? 'green--text text--darken-3'
                      : 'grey--text text--darken-3'
                  "
                >
                  He leído y acepto los términos de:
                </div>
                <div class="caption font-weight-bold primary--text">
                  {{ currentDoc && currentDoc.Title }}
                </div>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Lado Derecho: Navegación Anterior / Siguiente / Finalizar -->
        <div class="d-flex align-center gap-3">
          <!-- Botón Anterior -->
          <v-btn
            v-if="activeDocIndex > 0"
            outlined
            color="grey darken-2"
            large
            rounded
            class="text-none font-weight-bold px-4"
            @click="goToPreviousDoc"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Anterior
          </v-btn>

          <!-- Botón Siguiente (Claramente visible siempre) -->
          <v-btn
            v-if="activeDocIndex < documentsList.length - 1"
            :color="currentDocAccepted ? 'primary' : 'grey darken-1'"
            :outlined="!currentDocAccepted"
            large
            rounded
            class="text-none font-weight-bold px-6 elevation-2 transition-all btn-kiosk-nav"
            :style="
              !currentDocAccepted
                ? 'background-color: #f5f5f5 !important; color: #424242 !important; border: 2px solid #9e9e9e !important; opacity: 1 !important;'
                : 'font-weight: 800;'
            "
            :disabled="!currentDocAccepted"
            @click="goToNextDoc"
          >
            <span>Siguiente Documento</span>
            <v-icon
              right
              :color="currentDocAccepted ? 'white' : 'grey darken-3'"
              >{{
                currentDocAccepted ? "mdi-arrow-right-bold" : "mdi-lock-outline"
              }}</v-icon
            >
          </v-btn>

          <!-- Botón Finalizar (Solo en el último documento) -->
          <v-btn
            v-else
            :color="canFinishAllDocs ? 'success' : 'grey darken-1'"
            :outlined="!canFinishAllDocs"
            large
            rounded
            class="text-none font-weight-black px-6 elevation-3 btn-kiosk-nav"
            :style="
              !canFinishAllDocs
                ? 'background-color: #f5f5f5 !important; color: #424242 !important; border: 2px solid #9e9e9e !important; opacity: 1 !important;'
                : 'font-weight: 900;'
            "
            :disabled="!canFinishAllDocs"
            @click="completeAllDocuments"
          >
            <v-icon
              left
              :color="canFinishAllDocs ? 'white' : 'grey darken-3'"
              >{{
                canFinishAllDocs ? "mdi-check-all" : "mdi-lock-outline"
              }}</v-icon
            >
            <span>FINALIZAR Y ENVIAR A RR.HH.</span>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- 6. ESTADO 4: ATENCIÓN ACTIVA / DOCUMENTOS VERIFICADOS (MANTENIDO HASTA LIBERACIÓN MANUAL) -->
    <div
      v-else-if="kioskState === 'SUCCESS'"
      class="flex-grow-1 d-flex align-center justify-center bg-kiosk-idle pa-6 overflow-y-auto"
    >
      <v-card
        class="pa-8 rounded-2xl elevation-10 text-center max-w-700 my-auto"
      >
        <!-- Badge de Estado de Documentos -->
        <div class="d-flex justify-center mb-3">
          <v-chip
            color="green lighten-5"
            text-color="green darken-3"
            class="font-weight-black px-4 py-2"
            large
          >
            <v-icon left color="green darken-3">mdi-check-decagram</v-icon>
            DOCUMENTOS VERIFICADOS Y ACEPTADOS ({{ completedDocsCount }}/{{
              documentsList.length
            }})
          </v-chip>
        </div>

        <h2 class="text-h4 font-weight-black green--text text--darken-3 mb-1">
          ¡Revisión Completada!
        </h2>

        <!-- Ficha del Colaborador -->
        <div class="subtitle-1 grey--text text--darken-3 mb-4">
          Colaborador:
          <strong>{{
            (currentTurn && currentTurn.FullName) || "Colaborador"
          }}</strong>
          <span class="mx-2">•</span>
          DNI:
          <strong class="font-mono">{{
            (currentTurn && currentTurn.Dni) || "-"
          }}</strong>
          <span class="mx-2">•</span>
          Puesto:
          <strong>{{
            (currentTurn && currentTurn.PositionName) || "Operario"
          }}</strong>
        </div>

        <!-- Alerta de Atención en Ventanilla -->
        <v-alert
          type="info"
          text
          prominent
          class="text-left font-weight-medium mb-5 rounded-xl border"
        >
          <div class="font-weight-bold mb-1">Paso de Lectura Finalizado:</div>
          Todos los documentos del lote fueron verificados con éxito. Continúe
          con el <strong>Operador de Recursos Humanos</strong> para la firma y
          la posterior activación de su cuenta de trabajador.
        </v-alert>

        <!-- Bloque de Activación de ARI Sunshine en la Tablet -->
        <v-card
          outlined
          class="pa-5 rounded-xl mb-5 text-left bg-blue-grey-lighten-5 border-ari"
        >
          <div
            class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3"
          >
            <div class="d-flex align-center">
              <v-avatar size="42" color="primary" class="mr-3">
                <v-icon color="white">mdi-cellphone-check</v-icon>
              </v-avatar>
              <div>
                <h3 class="subtitle-1 font-weight-black primary--text mb-0">
                  Activación de Cuenta — ARI Sunshine
                </h3>
                <span class="caption grey--text text--darken-2">
                  Portal de autoservicio para boletas de pago digitales
                </span>
              </div>
            </div>
            <v-chip small color="primary" outlined class="font-weight-bold">
              Aplicativo Móvil / Web
            </v-chip>
          </div>

          <v-row class="align-center" no-gutters>
            <v-col cols="12" sm="auto" class="text-center mb-3 mb-sm-0 pr-sm-4">
              <div class="qr-ari-kiosk-box pa-2 bg-white rounded-lg border elevation-1 d-inline-block">
                <qrcode-vue
                  v-if="ariQrValue"
                  :value="ariQrValue"
                  :size="140"
                  level="Q"
                  render-as="svg"
                />
                <v-icon v-else size="100" color="grey lighten-2">mdi-qrcode</v-icon>
                <div class="caption font-weight-bold grey--text text--darken-3 mt-1" style="font-size: 10px;">
                  ESCANEAR CON CELULAR
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="" class="pl-sm-2">
              <div class="subtitle-2 font-weight-bold primary--text mb-1">
                Acceso Rápido con Código QR
              </div>
              <p class="body-2 grey--text text--darken-3 mb-2">
                Escanee este código QR con la cámara de su celular para registrarse o iniciar sesión en el portal <strong>ARI Sunshine</strong>. También puede pulsar el botón para acceder directamente desde esta tablet.
              </p>
              <div class="caption grey--text text--darken-2 font-mono mb-3">
                DNI: <strong>{{ (currentTurn && currentTurn.Dni) || '-' }}</strong>
              </div>
              <div>
                <v-btn
                  color="primary"
                  rounded
                  class="font-weight-bold text-none px-5 elevation-2"
                  @click="openAriSunshine"
                >
                  <v-icon left>mdi-open-in-new</v-icon>
                  Abrir ARI Sunshine en esta Tablet
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-divider class="mb-4" />

        <!-- Control Exclusivo de RRHH: Mensaje Informativo para el Colaborador -->
        <div class="pa-3 rounded-lg grey lighten-4 d-flex align-center justify-center text-center">
          <v-icon color="primary" small class="mr-2">mdi-information-outline</v-icon>
          <span class="caption grey--text text--darken-3 font-weight-medium">
            Esta terminal permanecerá activa para la firma y uso de ARI Sunshine. Se liberará automáticamente cuando el operador de RR.HH. finalice su atención.
          </span>
        </div>
      </v-card>
    </div>

    <!-- MODAL: ¿NECESITA MÁS TIEMPO DE LECTURA? -->
    <v-dialog v-model="showTimeWarningModal" persistent max-width="480">
      <v-card class="pa-6 rounded-2xl text-center">
        <v-icon size="64" color="amber darken-2" class="mb-3"
          >mdi-timer-alert-outline</v-icon
        >
        <h3 class="text-h5 font-weight-bold text--primary mb-2">
          ¿Necesita más tiempo?
        </h3>
        <p class="body-1 grey--text text--darken-2 mb-4">
          Su tiempo de lectura está por finalizar. ¿Desea añadir
          <strong>2 minutos adicionales</strong> para terminar de revisar los
          documentos?
        </p>
        <v-card-actions class="justify-center gap-3">
          <v-btn
            color="primary"
            large
            rounded
            class="font-weight-bold px-6"
            :disabled="extraMinutesGranted > 0"
            @click="requestExtraTime"
          >
            <v-icon left>mdi-timer-plus</v-icon>
            Sí, agregar 2 minutos
          </v-btn>
          <v-btn
            text
            large
            rounded
            color="grey darken-2"
            @click="showTimeWarningModal = false"
          >
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MODAL: TIEMPO AGOTADO -->
    <v-dialog v-model="showExpiredModal" persistent max-width="480">
      <v-card class="pa-6 rounded-2xl text-center">
        <v-icon size="64" color="red accent-3" class="mb-3"
          >mdi-timer-off-outline</v-icon
        >
        <h3 class="text-h5 font-weight-bold red--text text--darken-2 mb-2">
          Tiempo Agotado
        </h3>
        <p class="body-1 grey--text text--darken-2 mb-4">
          El tiempo de lectura ha finalizado sin marcar todos los documentos.
          <strong>Será llamado nuevamente</strong> en los siguientes turnos para
          completar su revisión.
        </p>
      </v-card>
    </v-dialog>

    <!-- MODAL: SESIÓN REEMPLAZADA EN OTRA PANTALLA -->
    <v-dialog v-model="showSessionReplacedModal" persistent max-width="480">
      <v-card class="pa-6 rounded-2xl text-center">
        <v-icon size="64" color="orange darken-2" class="mb-3"
          >mdi-alert-decagram</v-icon
        >
        <h3 class="text-h5 font-weight-bold text--primary mb-2">
          Estación Abierta en Otra Pantalla
        </h3>
        <p class="body-1 grey--text text--darken-2 mb-4">
          {{
            sessionReplacedMessage ||
            "Este Terminal fue asignado e iniciado en otro dispositivo."
          }}
        </p>
        <v-btn
          color="primary"
          rounded
          large
          @click="
            showScreenConfigModal = true;
            showSessionReplacedModal = false;
          "
        >
          Elegir Otro Terminal
        </v-btn>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import DocumentSigningSignalrService from "@/services/HumanResource/DocumentSigningSignalrService.js";
import ContractSignService from "@/services/HumanResource/ContractSignService.js";

// Formatos reales anexados al contrato
import FormatRisst from "@/views/HumanResource/PdfContract/Formats/FormatRisst.vue";
import FormatRit from "@/views/HumanResource/PdfContract/Formats/FormatRit.vue";
import FormatCodeOfConduct from "@/views/HumanResource/PdfContract/Formats/FormatCodeOfConduct.vue";
import FormatAddressDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatAddressDeclaration.vue";
import FormatDiningDeduction from "@/views/HumanResource/PdfContract/Formats/FormatDiningDeduction.vue";
import FormatBackgroundDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatBackgroundDeclaration.vue";
import FormatSubstanceDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatSubstanceDeclaration.vue";
import FormatOvertimeCompensation from "@/views/HumanResource/PdfContract/Formats/FormatOvertimeCompensation.vue";
import FormatDigitalConsentAri from "@/views/HumanResource/PdfContract/Formats/FormatDigitalConsentAri.vue";
import FormatDigitalConsentAriDeliverable from "@/views/HumanResource/PdfContract/Formats/FormatDigitalConsentAriDeliverable.vue";
import FormatSSTCritical from "@/views/HumanResource/PdfContract/Formats/FormatSSTCritical.vue";
import PdfContractPorTemporada from "@/views/HumanResource/PdfContract/PdfContractPorTemporada.vue";
import PdfContractPorIncrementoActividades from "@/views/HumanResource/PdfContract/PdfContractPorIncrementoActividades.vue";
import PdfContractPorServicioEspecifico from "@/views/HumanResource/PdfContract/PdfContractPorServicioEspecifico.vue";
import _sPositionDocument from "@/services/HumanResource/HmnPositionDocumentService.js";
import _sPositionService from "@/services/General/PositionService.js";
import QrcodeVue from "qrcode.vue";

export default {
  name: "DocumentSigningKioskView",
  components: {
    FormatRisst,
    FormatRit,
    FormatCodeOfConduct,
    FormatAddressDeclaration,
    FormatDiningDeduction,
    FormatBackgroundDeclaration,
    FormatSubstanceDeclaration,
    FormatOvertimeCompensation,
    FormatDigitalConsentAri,
    FormatDigitalConsentAriDeliverable,
    FormatSSTCritical,
    PdfContractPorTemporada,
    PdfContractPorIncrementoActividades,
    PdfContractPorServicioEspecifico,
    QrcodeVue,
  },
  data() {
    return {
      screenId: "",
      showScreenConfigModal: false,
      predefinedScreens: [
        "Terminal 1",
        "Terminal 2",
        "Terminal 3",
        "Terminal 4",
        "Terminal 5",
      ],
      isSignalRConnected: false,
      currentUsername: "",
      isFullscreen: false,
      showSessionReplacedModal: false,
      sessionReplacedMessage: "",
      activeKiosksBackend: [],
      kioskInUseError: "",
      myTabId: "TAB_" + Math.random().toString(36).substring(2, 9),
      kioskPresenceChannel: null,

      // Estados de Terminal: IDLE, CALLED, READING, SUCCESS
      kioskState: "IDLE",
      currentTurn: null,
      fullContractData: null,

      // Timers
      toleranceTimeRemaining: 60,
      toleranceInterval: null,
      readingTimeRemaining: 300, // 5 minutos = 300s
      readingInterval: null,
      extraMinutesGranted: 0,
      showTimeWarningModal: false,
      showExpiredModal: false,
      showConfirmReleaseModal: false,

      // Documentos
      documentsList: [],
      activeDocIndex: 0,
      acceptedDocsMap: {}, // docId -> true
      docAudits: [], // Lista de auditoría por documento
      lastAuditPayload: null, // Guarda el payload de auditoría con horas de aceptación
      pollTimer: null,
      heartbeatInterval: null,
      turnCallCounts: {}, // TurnCode -> conteo de llamadas (máximo 2)

      // Resaltador de datos clave en el contrato
      highlightObserver: null,
      highlightDebounceTimer: null,
    };
  },
  computed: {
    currentDoc() {
      return this.documentsList[this.activeDocIndex] || null;
    },
    currentDocAccepted: {
      get() {
        return !!(
          this.currentDoc && this.acceptedDocsMap[this.currentDoc.DocumentId]
        );
      },
      set(val) {
        if (this.currentDoc) {
          this.$set(this.acceptedDocsMap, this.currentDoc.DocumentId, val);
        }
      },
    },
    completedDocsCount() {
      return Object.values(this.acceptedDocsMap).filter(Boolean).length;
    },
    canFinishAllDocs() {
      return (
        this.documentsList.length > 0 &&
        this.completedDocsCount === this.documentsList.length
      );
    },
    contractDate() {
      if (this.fullContractData && this.fullContractData.AgtBeginDate) {
        return this.fullContractData.AgtBeginDate;
      }
      return new Date();
    },
    ariQrValue() {
      const dni = (
        (this.currentTurn && this.currentTurn.Dni) ||
        (this.fullContractData && this.fullContractData.PrsDocumentNumber) ||
        ""
      ).trim();
      if (!dni) return "";
      try {
        if (this.$fun && this.$fun.encrypt) {
          return this.$fun.encrypt(dni);
        }
      } catch (e) {}
      return dni;
    },
  },
  watch: {
    activeDocIndex() {
      this.scrollToDocTop();
      this.triggerHighlight();
    },
    fullContractData: {
      deep: true,
      handler() {
        this.triggerHighlight();
      },
    },
    kioskState(val) {
      if (val === "READING") {
        this.triggerHighlight();
      }
    },
  },
  created() {
    let tabId = sessionStorage.getItem("SUNEXPERT_TAB_SESSION_ID");
    if (!tabId) {
      tabId = "TAB_" + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem("SUNEXPERT_TAB_SESSION_ID", tabId);
    }
    this.myTabId = tabId;
  },
  mounted() {
    this.initBroadcastChannel();
    this.initSession();

    this.queueUpdateHandler = (e) => {
      const data = e && e.detail;
      if (data) {
        const list = data.ActiveKiosks || data.activeKiosks || [];
        this.activeKiosksBackend = list;
        this.fetchQueueState();
      }
    };
    window.addEventListener("sunexpert-queue-updated", this.queueUpdateHandler);

    // Polling periódico de respaldo cada 10 segundos
    this.pollTimer = setInterval(this.fetchQueueState, 10000);

    // Latido (Heartbeat) de presencia activa cada 15 segundos
    this.heartbeatInterval = setInterval(() => {
      if (this.isSignalRConnected && this.screenId) {
        DocumentSigningSignalrService.kioskHeartbeat(
          this.screenId,
          this.myTabId,
        );
      }
    }, 15000);

    document.addEventListener("fullscreenchange", () => {
      this.isFullscreen = !!document.fullscreenElement;
    });

    // Reactivación inmediata al volver a la pestaña (evita retrasos por throttling de navegadores)
    this.tabVisibilityHandler = () => {
      if (document.visibilityState === "visible") {
        console.log(
          "🖥️ [Terminal] Pestaña reactivada/enfocada. Sincronizando estado...",
        );
        if (this.screenId) {
          DocumentSigningSignalrService.kioskHeartbeat(
            this.screenId,
            this.myTabId,
          );
        }
        this.fetchQueueState();
      }
    };
    document.addEventListener("visibilitychange", this.tabVisibilityHandler);
    window.addEventListener("focus", this.tabVisibilityHandler);

    this.initHighlightObserver();
  },
  beforeDestroy() {
    this.clearAllTimers();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.kioskPresenceChannel) this.kioskPresenceChannel.close();
    if (this.queueUpdateHandler) {
      window.removeEventListener(
        "sunexpert-queue-updated",
        this.queueUpdateHandler,
      );
    }
    if (this.tabVisibilityHandler) {
      document.removeEventListener(
        "visibilitychange",
        this.tabVisibilityHandler,
      );
      window.removeEventListener("focus", this.tabVisibilityHandler);
    }
    if (this.highlightObserver) {
      this.highlightObserver.disconnect();
      this.highlightObserver = null;
    }
    if (this.highlightDebounceTimer) {
      clearTimeout(this.highlightDebounceTimer);
      this.highlightDebounceTimer = null;
    }
    DocumentSigningSignalrService.disconnectKiosk();
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  },
  methods: {
    initBroadcastChannel() {
      if (typeof window.BroadcastChannel !== "undefined") {
        this.kioskPresenceChannel = new BroadcastChannel(
          "sunexpert_terminal_channel",
        );
        this.kioskPresenceChannel.onmessage = (event) => {
          if (!event || !event.data) return;
          const { type, screenId, senderId } = event.data;

          // Si otra pestaña en el mismo navegador tomó control de esta misma terminal
          if (
            type === "TERMINAL_CLAIMED" &&
            String(screenId).trim().toUpperCase() ===
              String(this.screenId).trim().toUpperCase() &&
            senderId !== this.myTabId
          ) {
            if (this.isSignalRConnected) {
              console.warn(
                "🖥️ [Terminal] Terminal reclamada en otra pestaña local:",
                screenId,
              );
              this.sessionReplacedMessage =
                "Esta terminal fue iniciada en otra pestaña de este navegador.";
              this.showSessionReplacedModal = true;
              this.isSignalRConnected = false;
              this.clearAllTimers();
              if (this.pollTimer) clearInterval(this.pollTimer);
              this.kioskState = "IDLE";
              this.currentTurn = null;
              this.fullContractData = null;
              this.documentsList = [];
              this.acceptedDocsMap = {};
              this.docAudits = [];
              this.showTimeWarningModal = false;
              this.showExpiredModal = false;
            }
          }
        };
      }
    },

    async initSession() {
      await this.fetchQueueState();
      const saved = localStorage.getItem("SUNEXPERT_KIOSK_ID");
      if (saved) {
        this.screenId = saved;
        // Intentar conectar con la sesión persistida de esta pestaña
        this.connectKioskSignalR(false);
      } else {
        this.screenId = "Terminal 1";
        this.showScreenConfigModal = true;
      }
    },

    isScreenOnlineInOtherMachine(screenName) {
      if (!screenName) return false;
      const target = String(screenName).trim().toUpperCase();
      // Si la terminal actualmente seleccionada tiene un error activo de estar en uso
      if (
        this.kioskInUseError &&
        String(this.screenId).trim().toUpperCase() === target
      ) {
        return true;
      }
      // Si es el terminal ya activo en esta misma sesión conectada, permitirlo
      if (
        this.isSignalRConnected &&
        String(this.screenId).trim().toUpperCase() === target
      ) {
        return false;
      }
      return (this.activeKiosksBackend || []).some((k) => {
        const id =
          k.KioskId ||
          k.kioskId ||
          k.ScreenId ||
          k.screenId ||
          (typeof k === "string" ? k : "");
        return String(id).trim().toUpperCase() === target;
      });
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen().catch(() => {});
        }
        this.isFullscreen = true;
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
        this.isFullscreen = false;
      }
    },
    isDocAccepted(id) {
      return this.acceptedDocsMap[id] || false;
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m.toString().padStart(2, "0")}:${s
        .toString()
        .padStart(2, "0")}`;
    },
    clearAllTimers() {
      if (this.toleranceInterval) clearInterval(this.toleranceInterval);
      if (this.readingInterval) clearInterval(this.readingInterval);
    },
    async saveAndConnectScreen(forceTakeover = false) {
      if (!this.screenId) return;

      if (forceTakeover) {
        this.myTabId = "TAB_" + Math.random().toString(36).substring(2, 9);
        sessionStorage.setItem("SUNEXPERT_TAB_SESSION_ID", this.myTabId);
      }

      // Notificar a otras pestañas en el mismo navegador para que se cierren de inmediato
      if (this.kioskPresenceChannel) {
        this.kioskPresenceChannel.postMessage({
          type: "TERMINAL_CLAIMED",
          screenId: this.screenId,
          senderId: this.myTabId,
        });
      }

      localStorage.setItem("SUNEXPERT_KIOSK_ID", this.screenId);
      this.kioskInUseError = "";
      this.showScreenConfigModal = false; // Cerrar modal inmediatamente
      this.connectKioskSignalR(forceTakeover);
      if (
        !document.fullscreenElement &&
        document.documentElement.requestFullscreen
      ) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    },
    connectKioskSignalR(forceTakeover = false) {
      console.log(
        "🖥️ [Terminal] Conectando Terminal vía SignalR:",
        this.screenId,
        "Force:",
        forceTakeover,
      );

      DocumentSigningSignalrService.connectKiosk(
        this.screenId,
        {
          onKioskAlreadyInUse: (msg) => {
            console.warn("🖥️ [Terminal] Terminal ya en uso:", msg);
            this.kioskInUseError =
              (msg && msg.message) ||
              msg ||
              "Este Terminal ya está activo y en uso en otro navegador.";
            const norm = String(this.screenId || "")
              .trim()
              .toUpperCase();
            if (
              !this.activeKiosksBackend.some(
                (k) =>
                  String(k.KioskId || k.kioskId || k)
                    .trim()
                    .toUpperCase() === norm,
              )
            ) {
              this.activeKiosksBackend.push({
                KioskId: this.screenId,
                IsBusy: true,
              });
            }
            this.showScreenConfigModal = true;
            this.isSignalRConnected = false;
          },
          onKioskRegisteredSuccess: (id) => {
            console.log("🖥️ [Terminal] Registro exitoso para:", id);
            this.kioskInUseError = "";
            this.showScreenConfigModal = false;
          },
          onTurnAssigned: (data) => {
            console.log(
              "🖥️ [Terminal] Turno asignado recibido por SignalR:",
              data,
            );
            this.handleTurnAssigned(data);
          },
          onReadingExtended: (data) => {
            console.log("🖥️ [Terminal] Tiempo de lectura extendido:", data);
            this.readingTimeRemaining += (data.ExtraMinutes || 2) * 60;
            this.extraMinutesGranted = data.ExtraMinutes || 2;
            this.showTimeWarningModal = false;
          },
          onKioskReset: () => {
            console.log("🖥️ [Terminal] Reset de Terminal a IDLE");
            this.resetKioskToIdle();
          },
          onSessionReplaced: (msg) => {
            console.warn(
              "🖥️ [Terminal] Sesión reemplazada en otra pantalla:",
              msg,
            );
            sessionStorage.removeItem("SUNEXPERT_TAB_SESSION_ID");
            this.sessionReplacedMessage =
              msg || "Esta estación fue abierta en otra pantalla o navegador.";
            this.isSignalRConnected = false;
            this.clearAllTimers();
            this.kioskState = "IDLE";
            this.currentTurn = null;
            this.fullContractData = null;
            this.documentsList = [];
            this.acceptedDocsMap = {};
            this.docAudits = [];
            this.showTimeWarningModal = false;
            this.showExpiredModal = false;
            const norm = String(this.screenId || "")
              .trim()
              .toUpperCase();
            if (
              !this.activeKiosksBackend.some(
                (k) =>
                  String(k.KioskId || k.kioskId || k)
                    .trim()
                    .toUpperCase() === norm,
              )
            ) {
              this.activeKiosksBackend.push({
                KioskId: this.screenId,
                IsBusy: true,
              });
            }
            this.kioskInUseError =
              msg || "Esta terminal fue tomada por otro navegador o equipo.";
            this.showScreenConfigModal = true;
            this.fetchQueueState();
          },
          onKioskExpiredMessage: (data) => {
            console.log("🖥️ [Terminal] Mensaje de tiempo expirado:", data);
            this.showExpiredModal = true;
            setTimeout(() => {
              this.showExpiredModal = false;
              this.resetKioskToIdle();
            }, 4000);
          },
          onKioskCompleteSuccess: () => {
            console.log("🖥️ [Terminal] Documentos completados con éxito");
            this.kioskState = "SUCCESS";
          },
          onQueueUpdated: (data) => {
            if (data) {
              const list = data.ActiveKiosks || data.activeKiosks || [];
              this.activeKiosksBackend = list;
            }
          },
        },
        this.myTabId,
        forceTakeover,
      ).then(() => {
        this.isSignalRConnected = true;
        console.log("🖥️ [Terminal] SignalR conectado exitosamente");
      });

      if (this.pollTimer) clearInterval(this.pollTimer);
      this.fetchQueueState();
      this.pollTimer = setInterval(this.fetchQueueState, 2000);
    },

    async fetchQueueState() {
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.getQueueState(userId);
        if (res && res.data) {
          const list = res.data.ActiveKiosks || res.data.activeKiosks || [];
          this.activeKiosksBackend = list;
          const activeCalls =
            res.data.ActiveCalls || res.data.activeCalls || [];

          // SOLAMENTE sincronizar y mostrar turno si ESTA ventana está debidamente conectada y autorizada
          if (
            this.isSignalRConnected &&
            !this.showSessionReplacedModal &&
            !this.showScreenConfigModal
          ) {
            const currentNorm = String(this.screenId || "")
              .trim()
              .toUpperCase();
            const myActive = activeCalls.find(
              (c) =>
                String(c.AssignedKiosk || c.assignedKiosk || "")
                  .trim()
                  .toUpperCase() === currentNorm,
            );

            if (myActive) {
              if (this.kioskState === "IDLE") {
                console.log(
                  "🖥️ [Terminal] Turno activo detectado en backend vía sincronización:",
                  myActive,
                );
                this.handleTurnAssigned(myActive);
              } else if (
                this.currentTurn &&
                this.currentTurn.TurnId &&
                myActive.TurnId &&
                this.currentTurn.TurnId !== myActive.TurnId
              ) {
                console.log(
                  "🖥️ [Terminal] Turno cambiado en backend. Reasignando:",
                  myActive,
                );
                this.resetKioskToIdle();
                this.$nextTick(() => {
                  this.handleTurnAssigned(myActive);
                });
              }
            } else {
              // Si no hay ningún turno asignado a este terminal en el servidor, pero la pantalla aún muestra un turno
              if (this.kioskState !== "IDLE" && this.kioskState !== "SUCCESS") {
                console.log(
                  "🖥️ [Terminal] Sin turno asignado en backend. Regresando a IDLE.",
                );
                this.resetKioskToIdle();
              }
            }
          }
        }
      } catch (e) {
        console.warn("🖥️ [Terminal] Error en fetchQueueState:", e);
      }
    },

    getContractComponentName(typeContract) {
      if (Number(typeContract) === 10)
        return "PdfContractPorIncrementoActividades";
      if (Number(typeContract) === 11) return "PdfContractPorTemporada";
      return "PdfContractPorServicioEspecifico";
    },

    async loadContractDetails(agtID, typeContract) {
      if (!agtID) return;
      const userId =
        (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
      try {
        const res = await ContractSignService.getContractById(
          agtID,
          userId,
          true,
        );
        if (res && res.data) {
          const contractObj = Array.isArray(res.data)
            ? res.data[0] || {}
            : res.data || {};
          this.fullContractData = contractObj;
          const functions = await _sPositionService.fuctionsPrimary(
            contractObj.PstID,
          );
          this.fullContractData.itemsFunciones = functions.data;

          console.log(
            "🖥️ [Terminal] Contrato cargado exitosamente:",
            this.fullContractData,
          );

          // 1. Documento de Contrato Principal
          const effectiveType = contractObj.TypeContract || typeContract;
          const contractDoc = {
            DocumentId: 1,
            Title: "Contrato de Trabajo",
            DocumentType: "CONTRATO_TRABAJO",
            ComponentName: this.getContractComponentName(effectiveType),
          };

          // 2. Formatos Anexados reales por Puesto desde la BD
          let formats = [];
          const pstId =
            contractObj.PstID ||
            (this.currentTurn && this.currentTurn.PstID) ||
            null;
          const agtId =
            contractObj.AgtID ||
            (this.currentTurn && this.currentTurn.AgtID) ||
            null;
          try {
            const resFormats = await _sPositionDocument.getByPosition(
              pstId,
              userId,
              agtId,
            );
            if (
              resFormats &&
              resFormats.data &&
              Array.isArray(resFormats.data) &&
              resFormats.data.length > 0
            ) {
              // Filtrar SOLAMENTE los formatos que tienen Componente Vue para lectura en Kiosko
              // y que coincidan con la planilla del contrato del colaborador
              formats = resFormats.data
                .filter((fmt) => {
                  if (!fmt.PdcComponentName || !fmt.PdcComponentName.trim()) {
                    return false;
                  }

                  // Si el formato tiene filtro de planilla (TypePayrollCSV), validar con el contrato del trabajador
                  if (
                    fmt.TypePayrollCSV &&
                    fmt.TypePayrollCSV.trim().length > 0
                  ) {
                    const allowedPayrolls = fmt.TypePayrollCSV.split(",")
                      .map((x) => x.trim().toLowerCase())
                      .filter(Boolean);

                    if (allowedPayrolls.length > 0) {
                      const cTypePayroll =
                        contractObj.TypePayroll !== null &&
                        contractObj.TypePayroll !== undefined
                          ? String(contractObj.TypePayroll).trim().toLowerCase()
                          : "";
                      const cLbrId = contractObj.LbrID
                        ? String(contractObj.LbrID).trim().toLowerCase()
                        : "";
                      const cTypeLaborRegime =
                        contractObj.TypeLaborRegime !== null &&
                        contractObj.TypeLaborRegime !== undefined
                          ? String(contractObj.TypeLaborRegime)
                              .trim()
                              .toLowerCase()
                          : "";

                      const isMatch = allowedPayrolls.some((val) => {
                        if (
                          cTypePayroll &&
                          (cTypePayroll === val ||
                            parseInt(cTypePayroll, 10) === parseInt(val, 10))
                        )
                          return true;
                        if (
                          cLbrId &&
                          (cLbrId === val ||
                            parseInt(cLbrId, 10) === parseInt(val, 10))
                        )
                          return true;
                        if (
                          cTypeLaborRegime &&
                          (cTypeLaborRegime === val ||
                            parseInt(cTypeLaborRegime, 10) ===
                              parseInt(val, 10))
                        )
                          return true;
                        return false;
                      });

                      if (!isMatch) {
                        console.log(
                          `🖥️ [Terminal] Formato "${
                            fmt.PdcDocumentTitle || fmt.PdcComponentName
                          }" omitido por no corresponder a la planilla del contrato (Requiere: ${
                            fmt.TypePayrollCSV
                          } | Contrato: ${
                            cTypePayroll || cLbrId || cTypeLaborRegime
                          })`,
                        );
                        return false;
                      }
                    }
                  }

                  return true;
                })
                .map((fmt, idx) => ({
                  DocumentId: idx + 2,
                  Title:
                    fmt.PdcDocumentTitle ||
                    fmt.PdcComponentName ||
                    "Formato Anexo",
                  DocumentType: "FORMATO_ANEXO",
                  ComponentName: fmt.PdcComponentName,
                }));
            }
          } catch (e) {
            console.warn(
              "🖥️ [Terminal] Error al consultar formatos de puesto:",
              e,
            );
          }

          // Asegurar que el formato de Consentimiento Digital ARI siempre esté incluido en los documentos de lectura
          const hasAriDoc = formats.some(
            (f) =>
              f.ComponentName === "FormatDigitalConsentAri" ||
              f.ComponentName === "FormatDigitalConsentAriDeliverable",
          );
          if (!hasAriDoc) {
            formats.push({
              DocumentId: formats.length + 2,
              Title: "Consentimiento ARI Sunshine",
              DocumentType: "FORMATO_ANEXO",
              ComponentName: "FormatDigitalConsentAri",
            });
          }

          // Solo se incluyen el contrato y los formatos requeridos para este puesto
          this.documentsList = [contractDoc, ...formats];
          if (this.activeDocIndex >= this.documentsList.length) {
            this.activeDocIndex = 0;
          }
        }
      } catch (err) {
        console.warn("🖥️ [Terminal] Error al cargar datos del contrato:", err);
      }
    },

    // 1. Asignación de Turno -> Inicia Tolerancia de 1 Minuto
    handleTurnAssigned(turnData) {
      const turn = turnData.Turn || turnData.turn || turnData;
      this.currentTurn = turn;
      this.kioskState = "CALLED";
      this.toleranceTimeRemaining = turnData.ToleranceSeconds || 60;

      // Cargar documentos del lote según modalidad del contrato
      const contractType = turn.TypeContract || 11;
      const contractCompName = this.getContractComponentName(contractType);

      if (
        turn.BatchPayload &&
        turn.BatchPayload.Documents &&
        turn.BatchPayload.Documents.length > 0
      ) {
        this.documentsList = turn.BatchPayload.Documents.filter(
          (d) => d.ComponentName && d.ComponentName.trim().length > 0,
        );
      } else {
        this.documentsList = [
          {
            DocumentId: 1,
            Title: "Contrato de Trabajo",
            DocumentType: "CONTRATO_TRABAJO",
            ComponentName: contractCompName,
          },
        ];
      }

      this.acceptedDocsMap = {};
      this.docAudits = [];
      this.activeDocIndex = 0;

      // Cargar contrato real y sus formatos anexos
      const effectiveAgtId = turn.AgtID || turn.agtID || turn.AgreementId;
      if (effectiveAgtId) {
        this.loadContractDetails(effectiveAgtId, contractType);
      }

      // Arrancar cuenta regresiva de 1 minuto
      this.clearAllTimers();
      this.toleranceInterval = setInterval(() => {
        this.toleranceTimeRemaining--;
        if (this.toleranceTimeRemaining <= 0) {
          this.clearAllTimers();
          // No-Show
          DocumentSigningSignalrService.kioskNoShow(
            this.screenId,
            this.currentTurn.TurnId,
          );
          this.resetKioskToIdle();
        }
      }, 1000);
    },

    // 2. Iniciar Lectura (Usuario presionó comenzar dentro del minuto)
    startReadingSession() {
      this.clearAllTimers();
      this.kioskState = "READING";
      this.readingTimeRemaining =
        (this.currentTurn && this.currentTurn.ReadingMinutes * 60) || 300; // 5 min
      this.extraMinutesGranted = 0;

      // Asegurar carga de contrato si no estuviera listo
      if (
        !this.fullContractData &&
        this.currentTurn &&
        this.currentTurn.AgtID
      ) {
        this.loadContractDetails(
          this.currentTurn.AgtID,
          this.currentTurn.TypeContract || 11,
        );
      }

      // Notificar al Hub que inició lectura
      DocumentSigningSignalrService.kioskStartReading(
        this.screenId,
        this.currentTurn.TurnId,
      );

      // Registrar viewed_at del primer documento
      this.recordDocViewed(this.documentsList[0]);

      // Arrancar timer de lectura de 5 minutos
      this.readingInterval = setInterval(() => {
        this.readingTimeRemaining--;

        // A los 30 segundos restantes y si aún no pidió extensión: mostrar advertencia
        if (
          this.readingTimeRemaining === 30 &&
          this.extraMinutesGranted === 0 &&
          !this.canFinishAllDocs
        ) {
          this.showTimeWarningModal = true;
        }

        // Si expira el tiempo sin marcar todos
        if (this.readingTimeRemaining <= 0) {
          this.clearAllTimers();
          this.showTimeWarningModal = false;
          DocumentSigningSignalrService.kioskReadingExpired(
            this.screenId,
            this.currentTurn.TurnId,
          );
        }
      }, 1000);
    },

    requestExtraTime() {
      this.readingTimeRemaining += 120;
      this.extraMinutesGranted = 2;
      this.showTimeWarningModal = false;
      DocumentSigningSignalrService.kioskExtendReading(
        this.screenId,
        this.currentTurn ? this.currentTurn.TurnId : "",
      );
    },

    isStepAccessible(targetIdx) {
      if (targetIdx <= this.activeDocIndex) return true;
      for (let i = 0; i < targetIdx; i++) {
        const doc = this.documentsList[i];
        if (!doc || !this.acceptedDocsMap[doc.DocumentId]) {
          return false;
        }
      }
      return true;
    },

    getStepIcon(idx, doc) {
      if (this.isDocAccepted(doc.DocumentId)) return "mdi-check-circle";
      if (this.activeDocIndex === idx) return "mdi-circle-slice-8";
      if (this.isStepAccessible(idx)) return "mdi-file-document-outline";
      return "mdi-lock";
    },

    getStepIconColor(idx, doc) {
      if (this.isDocAccepted(doc.DocumentId)) return "success";
      if (this.activeDocIndex === idx) return "primary";
      if (this.isStepAccessible(idx)) return "grey darken-1";
      return "grey lighten-1";
    },

    goToStep(targetIdx) {
      if (!this.isStepAccessible(targetIdx)) {
        if (this.$toastr && this.$toastr.warning) {
          this.$toastr.warning(
            "Debe leer y aceptar el documento actual antes de avanzar al siguiente.",
          );
        }
        return;
      }
      this.activeDocIndex = targetIdx;
      this.scrollToDocTop();
      if (this.documentsList[targetIdx]) {
        this.recordDocViewed(this.documentsList[targetIdx]);
      }
    },

    scrollToDocTop() {
      this.$nextTick(() => {
        // 1. Resetear scrollTop del contenedor interno
        const container = this.$refs.docScrollContainer;
        if (container) {
          container.scrollTop = 0;
          if (typeof container.scrollTo === "function") {
            container.scrollTo({ top: 0, left: 0, behavior: "auto" });
          }
        }

        // 2. Desplazar ancla superior al viewport
        if (
          this.$refs.docTopAnchor &&
          typeof this.$refs.docTopAnchor.scrollIntoView === "function"
        ) {
          this.$refs.docTopAnchor.scrollIntoView({
            block: "start",
            behavior: "auto",
          });
        }

        // 3. Resetear scroll general de window y document
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        // 4. Doble verificación tras renderizado asíncrono de componentes de contrato
        setTimeout(() => {
          if (this.$refs.docScrollContainer) {
            this.$refs.docScrollContainer.scrollTop = 0;
          }
          if (
            this.$refs.docTopAnchor &&
            typeof this.$refs.docTopAnchor.scrollIntoView === "function"
          ) {
            this.$refs.docTopAnchor.scrollIntoView({
              block: "start",
              behavior: "auto",
            });
          }
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 80);
      });
    },

    initHighlightObserver() {
      if (this.highlightObserver || typeof MutationObserver === "undefined")
        return;
      this.highlightObserver = new MutationObserver((mutations) => {
        // Evitar bucle infinito ignorando mutaciones originadas por nuestro propio marcado
        const isSelfMutation = mutations.every((m) => {
          const target = m.target;
          return (
            (target &&
              target.classList &&
              target.classList.contains("kiosk-doc-highlight")) ||
            (target &&
              target.parentElement &&
              target.parentElement.classList &&
              target.parentElement.classList.contains("kiosk-doc-highlight"))
          );
        });
        if (!isSelfMutation) {
          this.triggerHighlight();
        }
      });
    },

    triggerHighlight() {
      if (this.highlightDebounceTimer) {
        clearTimeout(this.highlightDebounceTimer);
      }
      this.highlightDebounceTimer = setTimeout(() => {
        this.applyYellowHighlights();
      }, 160);
    },

    applyYellowHighlights() {
      this.$nextTick(() => {
        const container = this.$refs.docScrollContainer;
        if (!container || this.kioskState !== "READING") return;

        // Desconectar momentáneamente el observer mientras manipulamos el DOM
        if (this.highlightObserver) {
          this.highlightObserver.disconnect();
        }

        // 1. Limpiar marcas previas para asegurar idempotencia (salvo las generadas desde el servidor)
        try {
          const existingMarks = container.querySelectorAll(
            "mark.kiosk-doc-highlight:not(.kiosk-server-highlight)",
          );
          existingMarks.forEach((el) => {
            const parent = el.parentNode;
            if (parent) {
              parent.replaceChild(document.createTextNode(el.textContent), el);
              parent.normalize();
            }
          });
        } catch (e) {
          console.warn("Error al limpiar marcas previas:", e);
        }

        // 2. Extraer términos clave a resaltar
        const data = this.fullContractData || {};
        const turn = this.currentTurn || {};
        const terms = new Set();

        const name = (data.NtpFullName || turn.FullName || "").trim();
        if (name.length >= 4) terms.add(name);

        const dni = (data.PrsDocumentNumber || turn.Dni || "").trim();
        if (dni.length >= 6) terms.add(dni);

        const position = (data.PstName || turn.PositionName || "").trim();
        if (position.length >= 3) terms.add(position);

        const area = (data.DepName || data.AreaName || "").trim();
        if (area.length >= 3) terms.add(area);

        const address = (data.PrsAddress || "").trim();
        if (address.length >= 5) terms.add(address);

        // 2.1 Sueldos numéricos (mensual y diario si aplica)
        const salary = data.AgtSalary || data.Salary;
        if (salary) {
          const num = Number(salary);
          if (!isNaN(num) && num > 0) {
            const addSalaryValues = (val) => {
              terms.add(val.toFixed(2));
              terms.add(
                val.toLocaleString("es-PE", { minimumFractionDigits: 2 }),
              );
              terms.add(`S/. ${val.toFixed(2)}`);
              terms.add(`S/ ${val.toFixed(2)}`);
              terms.add(
                `S/. ${val.toLocaleString("es-PE", {
                  minimumFractionDigits: 2,
                })}`,
              );
              terms.add(
                `S/ ${val.toLocaleString("es-PE", {
                  minimumFractionDigits: 2,
                })}`,
              );
            };
            addSalaryValues(num);
            // Si es cálculo diario (ej. división entre 30 días)
            addSalaryValues(Math.round((num / 30) * 100) / 100);
          }
        }

        // 2.2 Fechas de Inicio y Fin de Contrato / Campaña
        const addDateVariants = (rawDate) => {
          if (!rawDate) return;
          const d = new Date(rawDate);
          if (isNaN(d.getTime())) {
            if (typeof rawDate === "string" && rawDate.trim().length >= 8) {
              terms.add(rawDate.trim());
            }
            return;
          }
          const day = String(d.getDate()).padStart(2, "0");
          const dayNum = String(d.getDate());
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = String(d.getFullYear());
          const months = [
            "enero",
            "febrero",
            "marzo",
            "abril",
            "mayo",
            "junio",
            "julio",
            "agosto",
            "setiembre",
            "octubre",
            "noviembre",
            "diciembre",
          ];
          const mName = months[d.getMonth()];
          const mNameCap = mName.charAt(0).toUpperCase() + mName.slice(1);

          terms.add(`${day}/${month}/${year}`);
          terms.add(`${dayNum}/${month}/${year}`);
          terms.add(`${day}-${month}-${year}`);
          terms.add(`${year}-${month}-${day}`);
          terms.add(`${day} de ${mName} de ${year}`);
          terms.add(`${day} de ${mName} del ${year}`);
          terms.add(`${dayNum} de ${mName} de ${year}`);
          terms.add(`${dayNum} de ${mName} del ${year}`);
          terms.add(`${day} de ${mNameCap} de ${year}`);
          terms.add(`${day} de ${mNameCap} del ${year}`);
        };

        addDateVariants(data.AgtBeginDate);
        addDateVariants(data.AgtEndDate);
        addDateVariants(data.InicioCampana);
        addDateVariants(data.FinCampana);
        addDateVariants(data.AgtExpirationDate);
        addDateVariants(turn.BeginDate);
        addDateVariants(turn.EndDate);

        const validTerms = Array.from(terms)
          .filter((t) => t && t.length >= 3)
          .sort((a, b) => b.length - a.length);

        const escapedTerms = validTerms.map((t) =>
          t
            .replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")
            .replace(/\\\s+/g, "\\s+"),
        );

        // 2.3 Patrones Regex enriquecidos:
        // - Frase completa de remuneración con cifras y letras (ej. S/. 2,000.00 soles (DOS MIL CON 00/100 SOLES))
        const fullSalaryPattern =
          "(?:S\\/\\.?\\s*\\d{1,4}(?:[.,]\\d{2,3})*(?:\\s*soles)?\\s*(?:\\([A-ZÁÉÍÓÚÑ\\s0-9.,/]+(?:CON\\s+\\d{1,2}\\/100|\\/100)?\\s*(?:SOLES|soles)?\\))|S\\/\\.?\\s*\\d{1,4}(?:[.,]\\d{2,3})*(?:\\s*soles)?)";
        // - Monto en letras con parentesis o sufijo (ej. (TREINTA Y SIETE CON 67/100 SOLES))
        const wordsSalaryPattern =
          "(?:\\([A-ZÁÉÍÓÚÑ\\s0-9.,]+(?:CON\\s+\\d{1,2}\\/100|\\/100)\\s*(?:SOLES|soles)?\\))";
        // - Monto en letras sin paréntesis (ej. TREINTA Y SIETE CON 67/100 SOLES)
        const rawWordsSalaryPattern =
          "(?:[A-ZÁÉÍÓÚÑ\\s0-9.,]+CON\\s+\\d{1,2}\\/100\\s*(?:SOLES|soles)?)";
        // - Fechas generales en formato dd/mm/yyyy o dd-mm-yyyy
        const generalDatePattern = "(?:\\b\\d{1,2}[/-]\\d{1,2}[/-]\\d{4}\\b)";
        // - Fechas escritas en texto (ej. 01 de octubre del 2026)
        const writtenDatePattern =
          "(?:\\b\\d{1,2}\\s+de\\s+(?:enero|febrero|marzo|abril|mayo|junio|julio|agosto|setiembre|septiembre|octubre|noviembre|diciembre)\\s+(?:de|del)\\s+\\d{4}\\b)";

        const allPatterns = [
          fullSalaryPattern,
          wordsSalaryPattern,
          rawWordsSalaryPattern,
          ...escapedTerms,
          generalDatePattern,
          writtenDatePattern,
        ].filter(Boolean);

        if (allPatterns.length === 0) return;

        const regex = new RegExp(`(${allPatterns.join("|")})`, "gi");

        // Recorrer árbol de texto dentro de .doc-content-wrapper
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode(node) {
              if (!node.nodeValue || !node.nodeValue.trim()) {
                return NodeFilter.FILTER_REJECT;
              }
              const parent = node.parentElement;
              if (!parent) return NodeFilter.FILTER_REJECT;
              const tag = parent.tagName.toUpperCase();
              if (
                tag === "SCRIPT" ||
                tag === "STYLE" ||
                tag === "BUTTON" ||
                tag === "SVG" ||
                tag === "MARK" ||
                tag === "INPUT" ||
                tag === "TEXTAREA" ||
                parent.classList.contains("kiosk-doc-highlight") ||
                parent.classList.contains("kiosk-server-highlight") ||
                parent.closest(".kiosk-step-pill") ||
                parent.closest(".kiosk-action-footer") ||
                parent.closest(".bg-worker-header")
              ) {
                return NodeFilter.FILTER_REJECT;
              }
              return NodeFilter.FILTER_ACCEPT;
            },
          },
          false,
        );

        const nodesToReplace = [];
        let currentNode;
        while ((currentNode = walker.nextNode())) {
          if (regex.test(currentNode.nodeValue)) {
            nodesToReplace.push(currentNode);
          }
        }

        nodesToReplace.forEach((node) => {
          const parent = node.parentNode;
          if (!parent) return;
          const text = node.nodeValue;
          regex.lastIndex = 0;

          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let match;

          while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) {
              fragment.appendChild(
                document.createTextNode(text.substring(lastIndex, match.index)),
              );
            }
            const mark = document.createElement("mark");
            mark.className = "kiosk-doc-highlight";
            mark.textContent = match[0];
            fragment.appendChild(mark);
            lastIndex = regex.lastIndex;
          }

          if (lastIndex < text.length) {
            fragment.appendChild(
              document.createTextNode(text.substring(lastIndex)),
            );
          }

          parent.replaceChild(fragment, node);
        });

        // Reconectar observer para monitorear renderizados asíncronos futuros
        if (this.highlightObserver && container) {
          this.highlightObserver.observe(container, {
            childList: true,
            subtree: true,
            characterData: false,
          });
        }
      });
    },

    goToNextDoc() {
      if (!this.currentDocAccepted) return;
      if (this.activeDocIndex < this.documentsList.length - 1) {
        this.activeDocIndex++;
        this.scrollToDocTop();
        if (this.currentDoc) {
          this.recordDocViewed(this.currentDoc);
        }
      }
    },

    goToPreviousDoc() {
      if (this.activeDocIndex > 0) {
        this.activeDocIndex--;
        this.scrollToDocTop();
        if (this.currentDoc) {
          this.recordDocViewed(this.currentDoc);
        }
      }
    },

    toggleAcceptanceCard() {
      if (!this.currentDoc) return;
      this.currentDocAccepted = !this.currentDocAccepted;
      this.onToggleCurrentDocAccepted();
    },

    onSelectDocTab(idx) {
      this.goToStep(idx);
    },

    handleDocScroll() {
      // Registrar scroll
    },

    getNowLocalString() {
      if (this.$moment) {
        return this.$moment().format("YYYY-MM-DD HH:mm:ss");
      }
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    recordDocViewed(doc) {
      if (!doc) return;
      let audit = this.docAudits.find((x) => x.DocumentId === doc.DocumentId);
      if (!audit) {
        audit = {
          DocumentId: doc.DocumentId,
          DocumentType: doc.DocumentType,
          Title: doc.Title,
          ComponentName: doc.ComponentName,
          ViewedAt: this.getNowLocalString(),
          AcceptedTermsAt: null,
          ReadingDurationSeconds: 0,
        };
        this.docAudits.push(audit);
      }
    },

    onToggleCurrentDocAccepted() {
      if (!this.currentDoc) return;
      const isAccepted = this.acceptedDocsMap[this.currentDoc.DocumentId];
      let audit = this.docAudits.find(
        (x) => x.DocumentId === this.currentDoc.DocumentId,
      );
      if (!audit) {
        this.recordDocViewed(this.currentDoc);
        audit = this.docAudits.find(
          (x) => x.DocumentId === this.currentDoc.DocumentId,
        );
      }
      if (audit) {
        audit.AcceptedTermsAt = isAccepted ? this.getNowLocalString() : null;
        console.log("audit.AcceptedTermsAt (Local):", audit.AcceptedTermsAt);
      }
    },

    // 3. Completar todos los documentos
    completeAllDocuments() {
      this.clearAllTimers();

      const turn = this.currentTurn || {};
      const nowLocal = this.getNowLocalString();
      const auditPayload = {
        BatchId:
          (turn.BatchPayload && turn.BatchPayload.BatchId) ||
          `BATCH-${Date.now()}`,
        EmployeeId: turn.Dni,
        SignedAt: nowLocal,
        IpAddress: "",
        SignaturePadId: "PAD-KIOSK",
        FingerprintHash: "",
        Status: "DOCUMENTS_ACCEPTED",
        Documents: (this.documentsList || []).map((d) => {
          const foundAudit = (this.docAudits || []).find(
            (x) => x.DocumentId === d.DocumentId,
          );
          return {
            DocumentId: d.DocumentId,
            DocumentType: d.DocumentType || "DOCUMENTO",
            Title: d.Title,
            ComponentName: d.ComponentName,
            ViewedAt: (foundAudit && foundAudit.ViewedAt) || nowLocal,
            AcceptedTermsAt:
              (foundAudit && foundAudit.AcceptedTermsAt) || nowLocal,
          };
        }),
      };

      this.lastAuditPayload = auditPayload;
      if (this.currentTurn) {
        this.currentTurn.AuditLog = auditPayload;
      }

      // 1. Mostrar pantalla de éxito inmediatamente
      this.kioskState = "SUCCESS";

      // 2. Guardar log de auditoría en backend por HTTP
      const userId =
        (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
      ContractSignService.saveAuditLog(auditPayload, userId).catch(() => {});

      console.warn("auditPayload", auditPayload);
      // 3. Notificar inmediatamente a SignalR y backend para que RRHH vea al colaborador listo de inmediato
      DocumentSigningSignalrService.kioskCompleteDocuments(
        this.screenId,
        turn.TurnId,
        auditPayload,
      );
      // ContractSignService.completeKioskDocuments(
      //   this.screenId,
      //   turn.TurnId,
      //   auditPayload,
      // ).catch(() => {});

      // 4. La sesión se mantiene activa con los datos del colaborador hasta que RR.HH. finalice la atención o libere la estación
      if (this.successTimeout) clearTimeout(this.successTimeout);
    },

    openAriSunshine() {
      // Abre el portal ARI Sunshine en una nueva pestaña / ventana
      window.open("https://ari.sunshineperu.com", "_blank");
    },

    manualReleaseTerminal() {
      this.showConfirmReleaseModal = true;
    },

    confirmManualRelease() {
      this.showConfirmReleaseModal = false;
      if (this.screenId) {
        DocumentSigningSignalrService.releaseKiosk(this.screenId);
      }
      this.resetKioskToIdle();
      if (this.$toastr && this.$toastr.success) {
        this.$toastr.success(
          "Estación liberada y lista para el siguiente turno.",
        );
      }
    },

    resetKioskToIdle() {
      this.clearAllTimers();
      if (this.successTimeout) clearTimeout(this.successTimeout);

      this.kioskState = "IDLE";
      this.currentTurn = null;
      this.fullContractData = null;
      this.lastAuditPayload = null;
      this.documentsList = [];
      this.acceptedDocsMap = {};
      this.docAudits = [];
      this.showTimeWarningModal = false;
      this.showExpiredModal = false;
      this.showConfirmReleaseModal = false;

      // Consultar de inmediato si hay un nuevo turno asignado en cola
      this.$nextTick(() => {
        this.fetchQueueState();
      });
    },
  },
};
</script>

<style scoped>
.kiosk-main-wrapper {
  background: #f4f6f9;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kiosk-reading-wrapper {
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.doc-scroll-container {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto !important;
  scroll-behavior: auto;
}

.bg-kiosk-idle {
  background: linear-gradient(135deg, #e0e7ff 0%, #f1f5f9 100%);
}

.kiosk-idle-box {
  background: white;
  border: 1px solid #cbd5e1;
  max-width: 550px;
}

.bg-kiosk-called {
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
}

.called-turn-box {
  background: white;
  border: 2px solid #f59e0b;
}

.max-w-700 {
  max-width: 700px;
  width: 100%;
}

.max-w-600 {
  max-width: 600px;
  width: 100%;
}

.tolerance-box {
  background: #fef2f2;
  border: 1px solid #fca5a5;
}

.font-mono {
  font-family: "Courier New", Courier, monospace;
}

.bg-doc-canvas {
  background: #e2e8f0;
}

.doc-content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.doc-content-wrapper.doc-content-wide {
  max-width: 1140px !important;
}

.kiosk-action-footer {
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.kiosk-stepper-bar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
}

.kiosk-step-pill {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
  user-select: none;
  white-space: nowrap;
}

.kiosk-step-pill.step-active {
  background: #e0e7ff;
  border-color: #4f46e5;
  color: #3730a3;
}

.kiosk-step-pill.step-completed {
  background: #ecfdf5;
  border-color: #10b981;
  color: #065f46;
}

.kiosk-step-pill.step-locked {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8fafc;
}

.border-accept {
  border: 2px solid #cbd5e1 !important;
  transition: all 0.2s ease-in-out;
}

.border-success {
  border-color: #10b981 !important;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.95;
    transform: scale(1.02);
  }
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

/* Resaltado amarillo de datos clave para el colaborador en quiosco */
::v-deep .kiosk-doc-highlight,
.kiosk-doc-highlight {
  background-color: #fef08a !important; /* Amarillo fluorescente suave de alta legibilidad */
  color: #0f172a !important; /* Texto oscuro de alto contraste */
  font-weight: 700 !important;
  padding: 1px 4px !important;
  border-radius: 3px !important;
  box-shadow: 0 1px 3px rgba(202, 138, 4, 0.25) !important;
  border-bottom: 2px solid #eab308 !important;
  display: inline !important;
  line-height: inherit !important;
}

@media print {
  ::v-deep .kiosk-doc-highlight,
  .kiosk-doc-highlight,
  ::v-deep .kiosk-server-highlight,
  .kiosk-server-highlight {
    background-color: transparent !important;
    background: transparent !important;
    color: inherit !important;
    box-shadow: none !important;
    border: none !important;
    border-bottom: none !important;
    padding: 0 !important;
  }
}

.border-ari {
  border: 1.5px solid #90caf9 !important;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%) !important;
}
</style>
